import { Logger } from '../../packages/logger/src/Logger';
import { ClientBuilderFactory } from '../remote/ClientBuilderFactory';
import { getMSTeamsContext } from '../util/MsTeamsContextUtil';
import { GraphURL } from '../util/URLUtils';
import { DirectorySearchUser, lookupByNameOrEmail, lookupByPhoneOrExtension } from './DirectorySearchService';
import {
  isGraphDirectoryForbidden,
  isGraphForbiddenError,
  isGraphForbiddenStatus,
  markGraphDirectoryForbidden,
} from './graphDirectoryAccess';

export interface IGraphUserEmailAddress {
  name: string;
  address: string;
}

export interface IGraphUser {
  displayName: string;
  id: string;
  mail: string;
  mobilePhone?: string;
  businessPhones?: string[];
  homePhones?: string[];
  emailAddresses?: IGraphUserEmailAddress[];
  givenName?: string;
  surname?: string;  // Used by both Users and Contacts APIs (lowercase)
  // Properties to help identify GAL-hidden users
  accountEnabled?: boolean;
  showInAddressList?: boolean;
  userType?: string;
  externalUserState?: string;
  /** Set when the user came from DSS (extension/phone search); used for avatar handling. */
  fromDss?: true;
  /** DSS response thumbnail URL; when set, use for avatar instead of Graph. */
  avatarUrl?: string;
  /** When set (DSS), phone numbers with correct type so UI can show Extension vs Mobile vs Work. */
  phoneNumbersWithType?: { type: string; value: string }[];
  /** Calling workspace from Directory Search. The dialpad lists these under Devices. */
  isWorkspaceDevice?: true;
}

export interface GraphAPIError {
  response?: {
    status?: number;
    data?: {
      error?: {
        code?: string;
        message?: string;
      };
    };
  };
}

/**
 * DSS Search API requires queryString min length 3 for name/email (Confluence Phase 2 API).
 * Shorter queries return 400; skip DSS and use Graph only for 1–2 character name/email search.
 */
const DSS_MIN_QUERY_LENGTH_NAME_EMAIL = 3;

function toGraphUserFromDirectory(user: DirectorySearchUser, id = user.id): IGraphUser {
  return {
    id,
    displayName: user.displayName,
    mail: user.mail ?? '',
    mobilePhone: user.mobilePhone,
    businessPhones: user.businessPhones,
    fromDss: true as const,
    ...(user.avatarUrl ? { avatarUrl: user.avatarUrl } : {}),
    ...(user.phoneNumbersWithType?.length ? { phoneNumbersWithType: user.phoneNumbersWithType } : {}),
    ...(user.isWorkspaceDevice ? { isWorkspaceDevice: true as const } : {}),
  };
}

/**
 * Constants for paginated search
 */
const SEARCH_BATCH_SIZE = 100;  // Number of users to fetch per batch
const SEARCH_MAX_BATCHES = 100; // Maximum number of batches (total 10000 users) - Auto-retries until found

/**
 * Search telemetry interface for performance tracking
 */
export interface SearchTelemetry {
  batchesFetched: number;
  usersScanned: number;
  searchDurationMs: number;
  matchesFound: number;
  searchType: 'Email' | 'Phone' | 'Name';
  query: string; // Masked for PII
  usedHybridSearch?: boolean;
  hybridSearchFallback?: boolean;
}

/**
 * Search result with telemetry
 */
export interface SearchResult {
  users: IGraphUser[];
  telemetry: SearchTelemetry;
}

/**
 * AbortController for canceling stale search requests
 */
let searchAbortController: AbortController | null = null;

/**
 * Extracts domain from email query for server-side filtering
 * @param {string} emailQuery - The email query
 * @returns {string | null} - Domain if found (e.g., "cisco.com"), null otherwise
 */
export function getEmailDomain(emailQuery: string): string | null {
  const trimmed = emailQuery.trim();
  
  // Case 1: Query starting with @ (e.g., "@cisco.com")
  if (trimmed.startsWith('@') && trimmed.length > 1) {
    const domainPart = trimmed.substring(1); // Remove leading @
    if (domainPart.length >= 3) {
      return domainPart;
    }
  }
  
  // Case 2: Email with @ symbol (extract domain after @)
  if (trimmed.includes('@') && trimmed.indexOf('@') > 0) {
    const parts = trimmed.split('@');
    const domain = parts[1];
    // Return domain if it has at least 3 characters for meaningful filtering
    if (domain && domain.length >= 3) {
      return domain;
    }
  }
  
  // Case 3: Domain-only query (e.g., "cisco.com", "sbx0624.onmicrosoft.com")
  // Check if it looks like a domain pattern (minimum 5 characters)
  if (trimmed.includes('.') && trimmed.length >= 5 && !trimmed.includes(' ') && /^[a-zA-Z0-9.-]+$/.test(trimmed)) {
    return trimmed;
  }
  
  return null;
}

/**
 * Builds optimized Graph API filter for email searches (server-side only)
 * NO pagination, NO client-side filtering
 * @param {string} emailQuery - The email query
 * @returns {string} - OData filter string for server-side filtering
 */
export function buildSmartEmailFilter(emailQuery: string): string {
  const trimmed = emailQuery.trim().toLowerCase();
  const escapedEmail = trimmed.replace(/'/g, "''");
  
  // For full email queries (contains @), search for exact match OR prefix match
  if (trimmed.includes('@')) {
    // Try exact match first, then fall back to startswith for partial matches
    return `accountEnabled eq true and (mail eq '${escapedEmail}' or startswith(mail,'${escapedEmail}'))`;
  }
  
  // For domain-only searches, match all emails in that domain
  const domain = getEmailDomain(trimmed);
  if (domain) {
    const escapedDomain = domain.replace(/'/g, "''");
    return `accountEnabled eq true and endswith(mail,'@${escapedDomain}')`;
  }
  
  // Fallback
  return `accountEnabled eq true and startswith(mail,'${escapedEmail}')`;
}

/**
 * Builds Graph API filter for phone searches (server-side only)
 * Uses endswith() to match last N digits
 * NO pagination, NO client-side filtering, NO directory crawling
 * @param {string} phoneQuery - The phone query
 * @returns {string} - OData filter string for server-side phone filtering
 */
export function buildPhoneFilter(phoneQuery: string): string {
  const digits = normalizePhoneNumber(phoneQuery);
  
  if (digits.length < 3) {
    return 'accountEnabled eq true'; // Return no results for insufficient digits
  }
  
  // Use last 4 digits for better precision (or all if less than 4)
  const searchDigits = digits.length >= 4 ? digits.slice(-4) : digits;
  const escapedDigits = searchDigits.replace(/'/g, "''");
  
  // Server-side filter: businessPhones/any OR mobilePhone endswith
  return `accountEnabled eq true and (businessPhones/any(p:endswith(p,'${escapedDigits}')) or endswith(mobilePhone,'${escapedDigits}'))`;
}

/**
 * Masks PII (Personally Identifiable Information) in query strings for logging
 * @param {string} query - The search query to mask
 * @returns {string} - Masked query safe for logging
 */
export function maskPIIForLogging(query: string): string {
  if (!query) return '';
  
  // Mask email addresses: show first 2 chars + domain
  // example@domain.com -> ex****@domain.com
  if (isEmailQuery(query)) {
    const atIndex = query.indexOf('@');
    if (atIndex > 0) {
      const localPart = query.substring(0, atIndex);
      const domain = query.substring(atIndex);
      const maskedLocal = localPart.substring(0, Math.min(2, localPart.length)) + '****';
      return maskedLocal + domain;
    }
    // Domain-only search (e.g., "cisco.com" or "@cisco.com") - not PII, return as-is
    return query;
  }
  
  // Mask phone numbers: show last 4 digits only
  // 1234567890 -> ******7890
  if (isPhoneNumberQuery(query)) {
    const digits = query.replace(/\D/g, '');
    if (digits.length > 4) {
      return '******' + digits.slice(-4);
    }
    return '******';
  }
  
  // For name searches, return as-is (not PII)
  return query;
}

/**
 * Filters out users hidden from GAL searches
 * @param {IGraphUser[]} users - Array of users to filter
 * @returns {IGraphUser[]} - Filtered array excluding GAL-hidden users
 */
export function filterGALHiddenUsers(users: IGraphUser[]): IGraphUser[] {
  if (!users || !Array.isArray(users)) {
    return [];
  }
  
  return users.filter(user => {
    // Filter disabled accounts
    if (user.accountEnabled === false) return false;
    // Filter GAL-hidden users
    if (user.showInAddressList === false) return false;
    // Filter guest users
    if (user.userType === 'Guest') return false;
    return true;
  });
}

/**
 * Returns an MS Teams user's graph profile
 *
 * @param {string} userId Microsoft Teams user ID
 * @returns {Promise<IGraphUser>} user object returned from the graph API
 */
export async function getUserByID(userId: string): Promise<IGraphUser> {
    const graphClient = await ClientBuilderFactory.getGraphClient();
    const response = await graphClient.get(
        `${GraphURL.Users}/${userId}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType`
    );
    if (response.status === 200) {
        return response.data as IGraphUser;
    }
    return Promise.reject(response);
}

/**
 * Returns the current MS Teams user's graph profile
 *
 * @returns {Promise<IGraphUser>} current user object returned from the graph API
 */
export async function getCurrentUser(): Promise<IGraphUser> {
  const context = await getMSTeamsContext();
  if (!context?.user?.id) {
    throw new Error('userId is not defined');
  }
  return getUserByID(context.user.id);
}

/**
 * Detects if a query string appears to be an email address search
 * @param {string} query - The search query
 * @returns {boolean} - True if query appears to be an email address
 */
export function isEmailQuery(query: string): boolean {
  // Allow partial email searches: must contain @ symbol and have at least 2 characters
  // Examples: "john@", "john@company", "john.doe@company.com", "@cisco.com"
  const trimmed = query.trim();
  
  // Case 1: Regular email with @ (localpart@domain)
  if (trimmed.includes('@') && trimmed.length >= 2 && trimmed.indexOf('@') > 0) {
    return true;
  }
  
  // Case 2: Domain search starting with @ (e.g., "@cisco.com", "@sbx0624.onmicrosoft.com")
  if (trimmed.startsWith('@') && trimmed.length > 4) {
    const domainPart = trimmed.substring(1); // Remove leading @
    // Check if what follows @ looks like a domain
    if (domainPart.includes('.') && /^[a-zA-Z0-9.-]+$/.test(domainPart)) {
      return true;
    }
  }
  
  // Case 3: Domain-only search (e.g., "cisco.com", "sbx0624.onmicrosoft.com")
  // Must have at least one dot and look like a domain (minimum 5 characters)
  if (trimmed.includes('.') && trimmed.length >= 5 && !trimmed.startsWith('@')) {
    // Basic domain pattern: at least 2 parts separated by dot, no spaces
    const parts = trimmed.split('.');
    if (parts.length >= 2 && !trimmed.includes(' ') && /^[a-zA-Z0-9.-]+$/.test(trimmed)) {
      return true;
    }
  }
  
  return false;
}

/**
 * Detects if a query string appears to be a phone number or extension search.
 * Any all-digit query (e.g. "2", "23", "2378") is treated as phone/extension so we use DSS only
 * and never trigger Graph name search for partial digit input.
 * @param {string} query - The search query
 * @returns {boolean} - True if query is phone/extension (use DSS, skip Graph)
 */
export function isPhoneNumberQuery(query: string): boolean {
  const trimmed = query?.trim() ?? '';
  if (!trimmed) return false;
  // All-digit query is always phone/extension (avoids Graph name search for "23", "2378", etc.)
  if (/^\d+$/.test(trimmed)) return true;
  // Otherwise: at least 3 digits and majority digits (e.g. "+1-555-1234")
  const digitCount = (trimmed.match(/\d/g) || []).length;
  return digitCount >= 3 && (digitCount / trimmed.length) > 0.5;
}

/**
 * Detects if a query is phone or extension (numeric); used to route to DSS Search API instead of Graph.
 * @param {string} query - The search query
 * @returns {boolean} - True if query looks like a phone number or extension
 */
export function isPhoneOrExtensionQuery(query: string): boolean {
  return isPhoneNumberQuery(query);
}

/**
 * Normalizes a phone number by removing all non-digit characters
 * @param {string} phoneNumber - The phone number to normalize
 * @returns {string} - Phone number with only digits
 */
export function normalizePhoneNumber(phoneNumber: string): string {
  return phoneNumber?.replace(/\D/g, '') || '';
}

/**
 * Boosts users with exact email match to the top of results
 * Preserves original order for non-exact matches
 * @param {IGraphUser[]} users - Array of users to sort
 * @param {string} emailQuery - The email query to match
 * @returns {IGraphUser[]} - Sorted array with exact matches first
 */
function boostExactEmailMatch(users: IGraphUser[], emailQuery: string): IGraphUser[] {
  if (!users || users.length === 0) return users;
  
  const normalizedQuery = emailQuery.toLowerCase().trim();
  const exactMatches: IGraphUser[] = [];
  const otherUsers: IGraphUser[] = [];
  
  users.forEach(user => {
    if (user.mail && user.mail.toLowerCase() === normalizedQuery) {
      exactMatches.push(user);
    } else {
      otherUsers.push(user);
    }
  });
  
  return [...exactMatches, ...otherUsers];
}

/**
 * Filters users by email address match
 * @param {IGraphUser[]} users - Array of users to filter
 * @param {string} emailQuery - The email query to match
 * @returns {IGraphUser[]} - Users whose email addresses match the query
 */
export function filterByEmail(users: IGraphUser[], emailQuery: string): IGraphUser[] {
  const normalizedQuery = emailQuery.toLowerCase().trim();
  
  // Detect domain-only patterns
  const isDomainOnlyWithAt = normalizedQuery.startsWith('@'); // e.g., "@cisco.com"
  const isDomainOnlyPlain = !normalizedQuery.includes('@'); // e.g., "cisco.com"
  const isDomainOnly = isDomainOnlyWithAt || isDomainOnlyPlain;
  
  const matches = users.filter((user) => {
    // Check mail field
    if (user.mail) {
      const normalized = user.mail.toLowerCase();
      
      // Domain-only search
      if (isDomainOnly) {
        let domainToMatch: string;
        
        if (isDomainOnlyWithAt) {
          // "@cisco.com" → match emails ending with "@cisco.com"
          domainToMatch = normalizedQuery; // Keep the @
        } else {
          // "cisco.com" → match emails ending with "@cisco.com"
          domainToMatch = `@${normalizedQuery}`;
        }
        
        return normalized.endsWith(domainToMatch);
      }
      
      // Regular email search (partial or full)
      return normalized === normalizedQuery || normalized.includes(normalizedQuery);
    }
    
    return false;
  });
  
  return matches;
}

/**
 * Filters users by phone number match (businessPhones first, then mobilePhone)
 * @param {IGraphUser[]} users - Array of users to filter
 * @param {string} phoneQuery - The phone number query to match
 * @returns {IGraphUser[]} - Users whose phone numbers contain the query
 */
export function filterByPhoneNumber(users: IGraphUser[], phoneQuery: string): IGraphUser[] {
  const normalizedQuery = normalizePhoneNumber(phoneQuery);
  
  const matches = users.filter((user) => {
    // Check businessPhones first (priority)
    if (user.businessPhones && Array.isArray(user.businessPhones) && user.businessPhones.length > 0) {
      const businessMatch = user.businessPhones.some(phone => {
        const normalized = normalizePhoneNumber(phone);
        return normalized.includes(normalizedQuery);
      });
      if (businessMatch) return true;
    }
    
    // Then check mobilePhone
    if (user.mobilePhone) {
      const normalized = normalizePhoneNumber(user.mobilePhone);
      return normalized.includes(normalizedQuery);
    }
    
    return false;
  });
  
  return matches;
}

/**
 * Type for search state callback (UX feedback)
 */
export type SearchStateCallback = (state: {
  isSearching: boolean;
  batchesFetched: number;
  matchesFound: number;
  message?: string;
}) => void;

/**
 * Paginated search helper for email/phone searches with telemetry
 * Fetches users in batches until matches are found or max batches reached
 * 
 * @param {string} query - The search query
 * @param {(users: IGraphUser[], query: string) => IGraphUser[]} filterFn - Function to filter users
 * @param {string} searchType - Type of search for logging ('Email' or 'Phone')
 * @param {number} resultLimit - Maximum number of results to return
 * @param {SearchStateCallback} onStateChange - Optional callback for UX state updates
 * @returns {Promise<SearchResult>} - Matched users with telemetry
 */
async function paginatedSearch(
  query: string,
  filterFn: (users: IGraphUser[], query: string) => IGraphUser[],
  searchType: 'Email' | 'Phone' | 'Name',
  resultLimit: number,
  onStateChange?: SearchStateCallback
): Promise<SearchResult> {
  const searchStartTime = Date.now();
  const graphClient = await ClientBuilderFactory.getGraphClient();
  
  let allMatches: IGraphUser[] = [];
  let skipToken: string | null = null;
  let batchCount = 0;
  let totalUsersScanned = 0;
  
  // Build smart filter for email searches (combines first letter + domain filtering)
  const isEmail = searchType === 'Email';
  const filterString = isEmail ? buildSmartEmailFilter(query) : 'accountEnabled eq true';
  
  if (isEmail) {
    const domain = getEmailDomain(query);
    const isDomainOnlyWithAt = query.trim().startsWith('@');
    const isDomainOnlyPlain = !query.includes('@');
    const isDomainOnly = isDomainOnlyWithAt || isDomainOnlyPlain;
    
    if (isDomainOnly) {
      Logger.info(`[${searchType} Search] Domain-only search - Domain: '${domain}' - This reduces result set by ~95%!`);
    } else {
      Logger.info(`[${searchType} Search] Using smart filter - First letter: '${query[0]}', Domain: '${domain || 'none'}' - This reduces result set by ~95%!`);
    }
  }
  
  while (batchCount < SEARCH_MAX_BATCHES) {
    batchCount++;
    
    // UX Improvement: Show "Searching directory..." after batch 2
    if (batchCount === 2 && onStateChange) {
      onStateChange({
        isSearching: true,
        batchesFetched: batchCount,
        matchesFound: allMatches.length,
        message: 'Searching directory...'
      });
    }
    
    // Build URL with pagination and smart filtering
    let url = `${GraphURL.Users}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType&$count=true&$top=${SEARCH_BATCH_SIZE}&$filter=${filterString}`;
    
    if (skipToken) {
      url += `&$skiptoken=${skipToken}`;
    }
    
    try {
      // Add ConsistencyLevel header when using endswith() operator (required by Graph API)
      const headers: { [key: string]: string } = {};
      if (filterString.includes('endswith')) {
        headers['ConsistencyLevel'] = 'eventual';
      }
      
      const response = await graphClient.get(url, headers ? { headers } : undefined);
      
      if (response?.status !== 200) {
        const error = response as GraphAPIError;
        if (error.response?.status === 404) {
          break;
        }
        // Return with telemetry even on error
        const searchDuration = Date.now() - searchStartTime;
        return {
          users: allMatches.slice(0, resultLimit),
          telemetry: {
            batchesFetched: batchCount,
            usersScanned: totalUsersScanned,
            searchDurationMs: searchDuration,
            matchesFound: allMatches.length,
            searchType,
            query: maskPIIForLogging(query)
          }
        };
      }
      
      const users = response.data.value as IGraphUser[];
      totalUsersScanned += users.length;
      
      if (users.length === 0) break;
      
      // Apply GAL filtering
      const filteredByGAL = filterGALHiddenUsers(users);
      
      // Apply email/phone filtering
      const matches = filterFn(filteredByGAL, query);
      
      Logger.info(`[${searchType} Search] Batch ${batchCount}: Fetched ${users.length} | After GAL: ${filteredByGAL.length} | Matches: ${matches.length} | Total matches: ${allMatches.length + matches.length}`);
      
      if (matches.length > 0) {
        allMatches.push(...matches);
        
        // UX Improvement: Return partial results immediately
        if (onStateChange) {
          onStateChange({
            isSearching: true,
            batchesFetched: batchCount,
            matchesFound: allMatches.length,
            message: `Found ${allMatches.length} match${allMatches.length === 1 ? '' : 'es'}...`
          });
        }
        
        // If we have enough matches, stop fetching
        if (allMatches.length >= resultLimit) {
          Logger.info(`[${searchType} Search] Stopping early - found ${allMatches.length} matches (limit: ${resultLimit})`);
          break;
        }
      }
      
      // Check if there are more pages
      skipToken = response.data['@odata.nextLink'] 
        ? new URL(response.data['@odata.nextLink']).searchParams.get('$skiptoken')
        : null;
      
      if (!skipToken) break;
      
    } catch (error) {
      Logger.error(`[${searchType} Search] Error in batch ${batchCount}: ${error}`);
      break;
    }
  }
  
  const searchDuration = Date.now() - searchStartTime;
  
  Logger.info(`[${searchType} Search] Completed: ${batchCount} batches, ${totalUsersScanned} users scanned, ${searchDuration}ms, returning ${Math.min(allMatches.length, resultLimit)} of ${allMatches.length} matches`);
  
  // Return results with telemetry
  return {
    users: allMatches.slice(0, resultLimit),
    telemetry: {
      batchesFetched: batchCount,
      usersScanned: totalUsersScanned,
      searchDurationMs: searchDuration,
      matchesFound: allMatches.length,
      searchType,
      query: maskPIIForLogging(query)
    }
  };
}

/** Options for directory user search (e.g. Speed Dial prefers Graph then DSS). */
export interface FetchDirectoryUsersOptions {
  /** When true, try Graph API first for name/email; only use Search API (DSS) when Graph returns no results. */
  preferGraphForDirectory?: boolean;
}

/**
 * Resolves DSS directory user IDs to Microsoft Graph user IDs so that speed dial
 * and getUsersByIds can look up contacts correctly. DSS may return Webex directory
 * entity IDs; Graph API requires Azure AD object IDs. This looks up users by mail
 * or userPrincipalName and returns a map from DSS id to Graph id.
 *
 * @param dssUsers Directory search results from DSS
 * @returns Map from DSS user id to Graph user id (only entries that were resolved)
 */
async function resolveGraphIdsForDssUsers(
  dssUsers: DirectorySearchUser[]
): Promise<Map<string, string>> {
  const result = new Map<string, string>();
  if (!dssUsers?.length) return result;

  const withIdentifier = dssUsers.filter(
    (u) => (u.mail && u.mail.trim()) || (u.userPrincipalName && u.userPrincipalName.trim())
  );
  if (withIdentifier.length === 0) return result;
  if (isGraphDirectoryForbidden()) return result;

  const BATCH_SIZE = 5;
  const graphClient = await ClientBuilderFactory.getGraphClient();

  for (let i = 0; i < withIdentifier.length; i += BATCH_SIZE) {
    const batch = withIdentifier.slice(i, i + BATCH_SIZE);
    const clauses: string[] = [];
    const keyToDssId = new Map<string, string>();

    for (const u of batch) {
      const mail = u.mail?.trim();
      const upn = u.userPrincipalName?.trim();
      const odataEscape = (s: string) => s.replace(/'/g, "''");
      if (mail) {
        clauses.push(`mail eq '${odataEscape(mail)}'`);
        keyToDssId.set(mail.toLowerCase(), u.id);
      }
      if (upn && upn !== mail) {
        clauses.push(`userPrincipalName eq '${odataEscape(upn)}'`);
        keyToDssId.set(upn.toLowerCase(), u.id);
      }
    }

    if (clauses.length === 0) continue;

    const filter = clauses.join(' or ');
    const url = `${GraphURL.Users}?$select=id,mail,userPrincipalName&$filter=${encodeURIComponent(filter)}&$top=${BATCH_SIZE * 2}`;
    const headers: { [key: string]: string } = { ConsistencyLevel: 'eventual' };

    try {
      const response = await graphClient.get(url, { headers });
      if (isGraphForbiddenStatus(response?.status)) {
        markGraphDirectoryForbidden('resolveGraphIdsForDssUsers');
        return result;
      }
      if (response?.status !== 200 || !response?.data?.value) continue;

      const graphUsers = response.data.value as Array<{ id: string; mail?: string; userPrincipalName?: string }>;
      for (const gu of graphUsers) {
        const key = (gu.mail || gu.userPrincipalName || '').toLowerCase();
        if (!key) continue;
        const dssId = keyToDssId.get(key);
        if (dssId) result.set(dssId, gu.id);
      }
    } catch (err: any) {
      if (isGraphForbiddenError(err)) {
        markGraphDirectoryForbidden('resolveGraphIdsForDssUsers');
        return result;
      }
      Logger.info(`[DSS] resolveGraphIdsForDssUsers batch failed: ${err?.message ?? err}`);
    }
  }

  return result;
}

/**
 * Returns list of users where their first name, last name, display name, phone number, or email address matches the provided string
 * This is the backward-compatible API (returns IGraphUser[] without telemetry)
 *
 * @param {string} query name prefix, phone number, or email address to search for
 * @param {number} limit maximum number of results
 * @param {SearchStateCallback} onStateChange - Optional callback for UX state updates
 * @param {FetchDirectoryUsersOptions} options - Optional (e.g. preferGraphForDirectory for Speed Dial)
 * @returns {Promise<IGraphUser[]>} users who match the query string
 */
export async function fetchDirectoryUsers(
  query: string,
  limit = 100,
  onStateChange?: SearchStateCallback,
  options?: FetchDirectoryUsersOptions
): Promise<IGraphUser[]> {
  const result = await fetchDirectoryUsersWithTelemetry(query, limit, onStateChange, options);
  return result.users;
}

/**
 * Returns list of users with telemetry data (enhanced version)
 *
 * @param {string} query name prefix, phone number, or email address to search for
 * @param {number} limit maximum number of results
 * @param {SearchStateCallback} onStateChange - Optional callback for UX state updates
 * @param {FetchDirectoryUsersOptions} options - Optional (e.g. preferGraphForDirectory for Speed Dial)
 * @returns {Promise<SearchResult>} users who match the query string with telemetry
 */
export async function fetchDirectoryUsersWithTelemetry(
  query: string,
  limit = 100,
  onStateChange?: SearchStateCallback,
  options?: FetchDirectoryUsersOptions
): Promise<SearchResult> {
  const searchStartTime = Date.now();
  
  try {
    // Input validation
    if (!query?.trim()) {
      return {
        users: [],
        telemetry: {
          batchesFetched: 0,
          usersScanned: 0,
          searchDurationMs: 0,
          matchesFound: 0,
          searchType: 'Name',
          query: ''
        }
      };
    }

    const trimmedQuery = query.trim();
    
    // Cancel any previous search to prevent stale results
    if (searchAbortController) {
      searchAbortController.abort();
    }
    searchAbortController = new AbortController();
    const abortSignal = searchAbortController.signal;
    
    // Detect search type
    const isEmailSearch = isEmailQuery(trimmedQuery);
    const isPhoneSearch = !isEmailSearch && isPhoneNumberQuery(trimmedQuery);
    const preferGraph = options?.preferGraphForDirectory === true;
    
    Logger.info(`[Search] Query: "${maskPIIForLogging(trimmedQuery)}" | Type: ${isEmailSearch ? 'EMAIL' : isPhoneSearch ? 'PHONE' : 'NAME'} | Limit: ${limit} | preferGraphForDirectory: ${preferGraph}`);

    // EMAIL SEARCH: when preferGraphForDirectory try Graph first; else try DSS first (only if query length >= 3), fallback to Graph if no results
    if (isEmailSearch) {
      if (preferGraph) {
        try {
          const emailFilter = buildSmartEmailFilter(trimmedQuery);
          const url = `${GraphURL.Users}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType&$count=true&$top=${limit}&$filter=${emailFilter}`;
          const graphClient = await ClientBuilderFactory.getGraphClient();
          const headers: { [key: string]: string } = { 'ConsistencyLevel': 'eventual' };
          const response = await graphClient.get(url, { headers, signal: abortSignal });
          if (response?.status === 200) {
            const users = response.data.value as IGraphUser[];
            const filtered = filterGALHiddenUsers(users);
            const boosted = boostExactEmailMatch(filtered, trimmedQuery);
            if (boosted.length > 0) {
              Logger.info(`[Email Search] preferGraphForDirectory: Graph API: ${boosted.length} users`);
              return {
                users: boosted,
                telemetry: {
                  batchesFetched: 1,
                  usersScanned: users.length,
                  searchDurationMs: Date.now() - searchStartTime,
                  matchesFound: boosted.length,
                  searchType: 'Email',
                  query: maskPIIForLogging(trimmedQuery)
                }
              };
            }
          }
        } catch (error: any) {
          if (error.name !== 'AbortError' && error.name !== 'CanceledError') {
            Logger.info(`[Email Search] preferGraphForDirectory: Graph returned no results or error, falling back to DSS: ${error?.message ?? error}`);
          }
        }
      }
      if (trimmedQuery.length >= DSS_MIN_QUERY_LENGTH_NAME_EMAIL) {
        try {
          const dssUsers = await lookupByNameOrEmail(trimmedQuery, limit, 'EMAIL');
          if (dssUsers.length > 0) {
            const users: IGraphUser[] = dssUsers.map((u) => toGraphUserFromDirectory(u));
            Logger.info(`[Email Search] DSS Search API: ${users.length} users, using results`);
            return {
              users,
              telemetry: {
                batchesFetched: 1,
                usersScanned: users.length,
                searchDurationMs: Date.now() - searchStartTime,
                matchesFound: users.length,
                searchType: 'Email',
                query: maskPIIForLogging(trimmedQuery)
              }
            };
          }
        } catch (error: any) {
          Logger.info(`[Email Search] DSS returned empty or error, falling back to Graph: ${error?.message ?? error}`);
        }
      }

      // Fallback to Graph API
      if (isGraphDirectoryForbidden()) {
        return {
          users: [],
          telemetry: {
            batchesFetched: 0,
            usersScanned: 0,
            searchDurationMs: Date.now() - searchStartTime,
            matchesFound: 0,
            searchType: 'Email',
            query: maskPIIForLogging(trimmedQuery)
          }
        };
      }
      try {
        const emailFilter = buildSmartEmailFilter(trimmedQuery);
        const url = `${GraphURL.Users}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType&$count=true&$top=${limit}&$filter=${emailFilter}`;
        const graphClient = await ClientBuilderFactory.getGraphClient();

        const headers: { [key: string]: string } = { 'ConsistencyLevel': 'eventual' };
        const response = await graphClient.get(url, { headers, signal: abortSignal });

        if (isGraphForbiddenStatus(response?.status)) {
          markGraphDirectoryForbidden('email directory search');
        } else if (response?.status === 200) {
          const users = response.data.value as IGraphUser[];
          const filtered = filterGALHiddenUsers(users);
          const boosted = boostExactEmailMatch(filtered, trimmedQuery);
          Logger.info(`[Email Search] Graph fallback: ${users.length} users, ${filtered.length} after GAL filter`);
          return {
            users: boosted,
            telemetry: {
              batchesFetched: 1,
              usersScanned: users.length,
              searchDurationMs: Date.now() - searchStartTime,
              matchesFound: boosted.length,
              searchType: 'Email',
              query: maskPIIForLogging(trimmedQuery)
            }
          };
        }
      } catch (error: any) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          Logger.info(`[Email Search] Request canceled (stale search)`);
        } else if (isGraphForbiddenError(error)) {
          markGraphDirectoryForbidden('email directory search');
        } else {
          Logger.error(`[Email Search] Error: ${error}`);
        }
      }

      return {
        users: [],
        telemetry: {
          batchesFetched: 0,
          usersScanned: 0,
          searchDurationMs: Date.now() - searchStartTime,
          matchesFound: 0,
          searchType: 'Email',
          query: maskPIIForLogging(trimmedQuery)
        }
      };
    }
    
    // PHONE / EXTENSION SEARCH (DSS Search API – no Graph API)
    if (isPhoneSearch) {
      try {
        const dssUsers = await lookupByPhoneOrExtension(trimmedQuery, limit);
        const users: IGraphUser[] = dssUsers.map((u) => toGraphUserFromDirectory(u));
        const searchDurationMs = Date.now() - searchStartTime;
        Logger.info(`[Phone/Extension Search] DSS Search API: ${users.length} users in ${searchDurationMs}ms`);
        return {
          users,
          telemetry: {
            batchesFetched: 1,
            usersScanned: users.length,
            searchDurationMs,
            matchesFound: users.length,
            searchType: 'Phone',
            query: maskPIIForLogging(trimmedQuery)
          }
        };
      } catch (error: any) {
        if (error.name === 'AbortError' || error.name === 'CanceledError') {
          Logger.info(`[Phone/Extension Search] Request canceled (stale search)`);
        } else {
          Logger.error(`[Phone/Extension Search] Error: ${error}`);
        }
        return {
          users: [],
          telemetry: {
            batchesFetched: 0,
            usersScanned: 0,
            searchDurationMs: Date.now() - searchStartTime,
            matchesFound: 0,
            searchType: 'Phone',
            query: maskPIIForLogging(trimmedQuery)
          }
        };
      }
    }
    
    // NAME SEARCH: when preferGraphForDirectory try Graph first; else try DSS first only if query length >= 3, then fallback to Graph
    if (preferGraph && !isGraphDirectoryForbidden()) {
      try {
        const escapedQuery = encodeURIComponent(trimmedQuery.replace(/'/g, "''"));
        const url = `${GraphURL.Users}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType&$count=true&$top=${limit}&$filter=(startswith(givenName,'${escapedQuery}') or startswith(surname,'${escapedQuery}') or startswith(displayName,'${escapedQuery}')) and accountEnabled eq true`;
        const graphClient = await ClientBuilderFactory.getGraphClient();
        const response = await graphClient.get(url);
        if (isGraphForbiddenStatus(response?.status)) {
          markGraphDirectoryForbidden('name directory search (preferGraph)');
        } else if (response?.status === 200) {
          const users = response.data.value as IGraphUser[];
          const filtered = filterGALHiddenUsers(users);
          if (filtered.length > 0) {
            Logger.info(`[Name Search] preferGraphForDirectory: Graph API: ${filtered.length} users`);
            return {
              users: filtered,
              telemetry: {
                batchesFetched: 1,
                usersScanned: users.length,
                searchDurationMs: Date.now() - searchStartTime,
                matchesFound: filtered.length,
                searchType: 'Name',
                query: maskPIIForLogging(trimmedQuery)
              }
            };
          }
        }
      } catch (error: any) {
        if (isGraphForbiddenError(error)) {
          markGraphDirectoryForbidden('name directory search (preferGraph)');
        } else {
          Logger.info(`[Name Search] preferGraphForDirectory: Graph returned no results or error, falling back to DSS: ${error?.message ?? error}`);
        }
      }
    }
    if (trimmedQuery.length >= DSS_MIN_QUERY_LENGTH_NAME_EMAIL) {
      try {
        const dssUsers = await lookupByNameOrEmail(trimmedQuery, limit, 'NAME');
        if (dssUsers.length > 0) {
          const graphIdMap = await resolveGraphIdsForDssUsers(dssUsers);
          const users: IGraphUser[] = dssUsers.map((u) =>
            toGraphUserFromDirectory(u, graphIdMap.get(u.id) ?? u.id)
          );
          Logger.info(`[Name Search] DSS Search API: ${users.length} users, using results`);
          return {
            users,
            telemetry: {
              batchesFetched: 1,
              usersScanned: users.length,
              searchDurationMs: Date.now() - searchStartTime,
              matchesFound: users.length,
              searchType: 'Name',
              query: maskPIIForLogging(trimmedQuery)
            }
          };
        }
      } catch (error: any) {
        Logger.info(`[Name Search] DSS returned empty or error, falling back to Graph: ${error?.message ?? error}`);
      }
    }

    // Fallback to Graph API (or direct for name/email when query length < 3)
    if (isGraphDirectoryForbidden()) {
      return {
        users: [],
        telemetry: {
          batchesFetched: 0,
          usersScanned: 0,
          searchDurationMs: Date.now() - searchStartTime,
          matchesFound: 0,
          searchType: 'Name',
          query: maskPIIForLogging(trimmedQuery)
        }
      };
    }

    try {
      const escapedQuery = encodeURIComponent(trimmedQuery.replace(/'/g, "''"));
      const url = `${GraphURL.Users}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType&$count=true&$top=${limit}&$filter=(startswith(givenName,'${escapedQuery}') or startswith(surname,'${escapedQuery}') or startswith(displayName,'${escapedQuery}')) and accountEnabled eq true`;
      const graphClient = await ClientBuilderFactory.getGraphClient();
      const response = await graphClient.get(url);

      if (isGraphForbiddenStatus(response?.status)) {
        markGraphDirectoryForbidden('name directory search');
      } else if (response?.status === 200) {
        const users = response.data.value as IGraphUser[];
        const filtered = filterGALHiddenUsers(users);
        Logger.info(`[Name Search] Graph fallback: ${users.length} users`);
        return {
          users: filtered,
          telemetry: {
            batchesFetched: 1,
            usersScanned: users.length,
            searchDurationMs: Date.now() - searchStartTime,
            matchesFound: filtered.length,
            searchType: 'Name',
            query: maskPIIForLogging(trimmedQuery)
          }
        };
      }
      const error = response as GraphAPIError;
      if (error.response?.status === 404) {
        return {
          users: [],
          telemetry: {
            batchesFetched: 0,
            usersScanned: 0,
            searchDurationMs: Date.now() - searchStartTime,
            matchesFound: 0,
            searchType: 'Name',
            query: maskPIIForLogging(trimmedQuery)
          }
        };
      }
    } catch (error: any) {
      if (isGraphForbiddenError(error)) {
        markGraphDirectoryForbidden('name directory search');
      } else {
        Logger.error(`[Name Search] Graph fallback error: ${error}`);
      }
    }

    return {
      users: [],
      telemetry: {
        batchesFetched: 0,
        usersScanned: 0,
        searchDurationMs: Date.now() - searchStartTime,
        matchesFound: 0,
        searchType: 'Name',
        query: maskPIIForLogging(trimmedQuery)
      }
    };
  } catch (error) {
    Logger.error(`Error in fetchDirectoryUsers: ${error}`);
    return {
      users: [],
      telemetry: {
        batchesFetched: 0,
        usersScanned: 0,
        searchDurationMs: Date.now() - searchStartTime,
        matchesFound: 0,
        searchType: 'Name',
        query: maskPIIForLogging(query)
      }
    };
  }
}

/**
 * Enhanced search for directory users. Uses full query first (one API call) so full-name
 * search (e.g. "Ravichandra Ramakrishna") works with DSS/Graph. Falls back to per-term
 * search only if full-query returns no results.
 *
 * @param {string} query search query (can be multi-word like "John Smith", phone number, or email address)
 * @param {number} limit maximum number of results
 * @param {FetchDirectoryUsersOptions} options optional (e.g. preferGraphForDirectory for Speed Dial)
 * @returns {Promise<IGraphUser[]>} users who match the query
 */
export async function fetchDirectoryUsersEnhanced(
  query: string,
  limit = 100,
  options?: FetchDirectoryUsersOptions
): Promise<IGraphUser[]> {
  try {
    if (!query?.trim()) {
      return [];
    }

    // For email addresses, don't split - search as a single term
    if (isEmailQuery(query)) {
      return fetchDirectoryUsers(query, limit, undefined, options);
    }

    // For phone numbers, don't split - search as a single term
    if (isPhoneNumberQuery(query)) {
      return fetchDirectoryUsers(query, limit, undefined, options);
    }

    const trimmed = query.trim();
    const searchTerms = trimmed.split(/\s+/);

    // Single term: one search
    if (searchTerms.length === 1) {
      return fetchDirectoryUsers(trimmed, limit, undefined, options);
    }

    // Multi-term: try full query first (one API call for "Ravichandra Ramakrishna")
    const fullQueryResults = await fetchDirectoryUsers(trimmed, limit, undefined, options);
    if (fullQueryResults.length > 0) {
      return fullQueryResults;
    }

    // Fallback: per-term search and merge (only if full-query returned nothing)
    const allResults = new Map<string, IGraphUser>();
    const termMatchCounts = new Map<string, number>();

    for (const term of searchTerms) {
      const termResults = await fetchDirectoryUsers(term, limit, undefined, options);
      termResults.forEach(user => {
        allResults.set(user.id, user);
        termMatchCounts.set(user.id, (termMatchCounts.get(user.id) || 0) + 1);
      });
    }

    const filteredResults = Array.from(allResults.values()).filter(user => {
      const fullText = `${user.givenName || ''} ${user.surname || ''} ${user.displayName || ''}`.toLowerCase();
      return searchTerms.every(term => fullText.includes(term.toLowerCase()));
    });

    filteredResults.sort((a, b) => {
      const aMatches = termMatchCounts.get(a.id) || 0;
      const bMatches = termMatchCounts.get(b.id) || 0;
      if (aMatches !== bMatches) return bMatches - aMatches;
      const aName = a.displayName || `${a.givenName || ''} ${a.surname || ''}`;
      const bName = b.displayName || `${b.givenName || ''} ${b.surname || ''}`;
      return aName.localeCompare(bName);
    });

    return filteredResults.slice(0, limit);
  } catch (error) {
    console.error('Error in fetchDirectoryUsersEnhanced:', error);
    return fetchDirectoryUsers(query, limit, undefined, options);
  }
}

/**
 * Fetches a user's avatar using their user ID
 *
 * @param {string} userId user's image to fetch
 * @returns {Promise<string>} image url
 */
export async function fetchDirectoryUserAvatar(
  userId: string
): Promise<string | undefined> {
  try {
    const url = `/beta/users/${userId}/photo/$value`;
    const graphClient = await ClientBuilderFactory.getGraphClient();
    const response = await graphClient.get(url, {
      responseType: 'blob',
    });
    if (response.status === 200) return URL.createObjectURL(response.data);
    return undefined;
  } catch (error: any) {
    // 404 is expected when user has no photo - don't log as error
    if (error?.response?.status === 404) {
      return undefined;
    }
    // Log other errors for troubleshooting
    Logger.error(`Failed to fetch avatar for user ${userId}: ${error}`);
    return undefined;
  }
}

 //This code splits the ids list into batches of 15 and executes a separate query for each batch. 
//The results from each query are concatenated to produce the final result. 
//This approach can help to reduce the number of child clauses in the filter expression and avoid the error you encountered.
export async function getUsersByIds(
    ids:string[],
    limit = 100
  ): Promise<IGraphUser[]> {
    try {
      if(!ids || !Array.isArray(ids) || ids.length === 0) {
        return [];
      }

      // Filter out invalid IDs and remove duplicates
      const validIds = ids.filter(id => id && typeof id === 'string' && id.trim());
      if(validIds.length === 0) {
        return [];
      }

      // Remove duplicates to prevent exceeding MS Graph API limit of 15 OR clauses
      const uniqueIds = Array.from(new Set(validIds));
      if(uniqueIds.length === 0) {
        return [];
      }
      
      // Reducing noise - uncomment for debugging if needed
      // Log deduplication results for debugging
      // if (validIds.length !== uniqueIds.length) {
      //   console.log(`getUsersByIds: Deduplicated ${validIds.length} IDs down to ${uniqueIds.length} unique IDs to prevent MS Graph API limit error`);
      // }

      const chunkSize = 10; // Reduced from 15 to be more conservative with MS Graph API limits
      const chunks = [];
      for (let i = 0; i < uniqueIds.length; i += chunkSize) {
        chunks.push(uniqueIds.slice(i, i + chunkSize));
      }
      
      // Reducing noise - uncomment for debugging if needed
      // Debug logging for chunking
      // console.log(`getUsersByIds: Split ${uniqueIds.length} unique IDs into ${chunks.length} chunks of max ${chunkSize} IDs each`);
      // chunks.forEach((chunk, index) => {
      //   console.log(`getUsersByIds: Chunk ${index + 1} has ${chunk.length} IDs:`, chunk);
      // });
  
      const promises = chunks.map(async (chunk, index) => {
        const encodedValues = chunk.map(value => `'${encodeURIComponent(value)}'`);
        const userIds = `${encodedValues.join(',')}`;
        const url = `${GraphURL.Users}?$select=id,displayName,givenName,surname,mobilePhone,mail,businessPhones,accountEnabled,showInAddressList,userType&$count=true&$top=${limit}&$filter=id in (${userIds}) and accountEnabled eq true`;
        
        // Reducing noise - uncomment for debugging if needed
        // console.log(`getUsersByIds: Making API call ${index + 1}/${chunks.length} with ${chunk.length} IDs`);
        // console.log(`getUsersByIds: API URL for chunk ${index + 1}: ${url.substring(0, 200)}...`);
        
        const graphClient = await ClientBuilderFactory.getGraphClient();
        try {
          const response = await graphClient.get(url);
          if (response.status === 200) {
            const users = response.data.value as IGraphUser[];
            const filteredUsers = filterGALHiddenUsers(users);
            // Reducing noise - uncomment for debugging if needed
            // console.log(`getUsersByIds: Chunk ${index + 1} returned ${users.length} users, ${filteredUsers.length} after filtering`);
            // console.log(`getUsersByIds: Chunk ${index + 1} user details:`, users.map(u => ({ id: u.id, displayName: u.displayName, accountEnabled: u.accountEnabled })));
            return filteredUsers;
          }
          // Reducing noise - uncomment for debugging if needed
          // console.log(`getUsersByIds: Chunk ${index + 1} failed with status: ${response.status}`);
          return [];
        } catch (error) {
          console.error(`getUsersByIds: Chunk ${index + 1} threw error:`, error);
          console.error(`getUsersByIds: Error details:`, JSON.stringify(error, null, 2));
          return [];
        }
      });
  
      const results = await Promise.all(promises);
      const flatResults = results.flat();
      // Reducing noise - uncomment for debugging if needed
      // console.log(`getUsersByIds: Final results - ${flatResults.length} total users returned from all chunks`);
      // if (flatResults.length > 0) {
      //   console.log(`getUsersByIds: Final user sample:`, flatResults.slice(0, 3).map(u => ({ id: u.id, displayName: u.displayName })));
      // }
      return flatResults;
    } catch (error) {
      return [];
    }
  }